// 🔒 GLOBAL-SEARCH-PALETTE-2026-05-31
// Buscador global Cmd+K / Ctrl+K. Busca en facturas POS, facturas directas,
// productos, clientes y navega al resultado.

import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, FileText, ReceiptText, Package, Users, Loader2, X, CornerDownLeft, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GROUP_ICONS = {
  bills: ReceiptText,
  invoices: FileText,
  products: Package,
  customers: Users,
};

const GROUP_ROUTES = {
  bills: '/billing',
  invoices: '/accounting',
  products: '/inventory',
  customers: '/accounting',
};

function fmt(n) {
  if (n === null || n === undefined) return '—';
  try {
    return new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n));
  } catch {
    return String(n);
  }
}

export default function GlobalCommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Cmd+K / Ctrl+K listener global
  useEffect(() => {
    const handler = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmd = isMac ? e.metaKey : e.ctrlKey;
      if (cmd && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((prev) => !prev);
        return;
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Focus input al abrir
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQ('');
      setResults(null);
      setError(null);
      setActiveIndex(0);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!q || q.length < 2) {
      setResults(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = {};
        const tok = sessionStorage.getItem('token') || localStorage.getItem('token');
        if (tok) headers.Authorization = `Bearer ${tok}`;
        const r = await axios.get(`${API}/accounting/search/global`, {
          params: { q },
          headers,
        });
        setResults(r.data);
        setActiveIndex(0);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const flatItems = (results?.groups || []).flatMap((g) =>
    g.items.map((it) => ({ ...it, _group: g.type, _label: g.label }))
  );

  const onSelect = useCallback(
    (item) => {
      const route = GROUP_ROUTES[item._group] || '/dashboard';
      setOpen(false);
      // Navegar con state para que la página destino pueda filtrar
      navigate(route, { state: { searchQuery: q, selectedId: item.id, group: item._group } });
    },
    [navigate, q]
  );

  const onKeyDown = (e) => {
    if (!flatItems.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = flatItems[activeIndex];
      if (item) onSelect(item);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[10vh] px-3 sm:px-4"
      onClick={() => setOpen(false)}
      data-testid="global-cmdk-overlay"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="global-cmdk-modal"
      >
        {/* Input */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <Search size={18} className="text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar facturas, NCF, RNC, clientes, productos…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground min-h-[36px]"
            data-testid="global-cmdk-input"
          />
          <kbd className="hidden sm:inline-block text-[10px] font-mono px-1.5 py-0.5 rounded border border-border bg-muted/30 text-muted-foreground">ESC</kbd>
          <button
            onClick={() => setOpen(false)}
            className="text-muted-foreground hover:text-foreground p-1"
            data-testid="global-cmdk-close"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto">
          {!q && (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground" data-testid="global-cmdk-empty">
              <Search size={28} className="mx-auto opacity-30 mb-2" />
              <p>Escribe al menos 2 caracteres para buscar en facturas, clientes, productos…</p>
              <p className="mt-1 opacity-60">Atajo: <kbd className="px-1 border border-border rounded font-mono">Cmd</kbd> + <kbd className="px-1 border border-border rounded font-mono">K</kbd></p>
            </div>
          )}
          {q && q.length < 2 && (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">
              Mínimo 2 caracteres
            </div>
          )}
          {loading && (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" size={14} /> Buscando…
            </div>
          )}
          {error && (
            <div className="px-4 py-6 text-center text-xs text-rose-600">
              Error: {error}
            </div>
          )}
          {!loading && !error && results && results.total_results === 0 && (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground" data-testid="global-cmdk-noresults">
              Sin resultados para “<strong>{q}</strong>”
            </div>
          )}
          {!loading && results && results.groups?.map((group, gi) => {
            const Icon = GROUP_ICONS[group.type] || FileText;
            // calc absolute index offset for keyboard
            const itemsBefore = results.groups.slice(0, gi).reduce((s, g) => s + g.items.length, 0);
            return (
              <div key={group.type} data-testid={`global-cmdk-group-${group.type}`}>
                <div className="sticky top-0 bg-muted/40 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 border-y border-border">
                  <Icon size={11} />
                  {group.label} <span className="opacity-60">({group.count})</span>
                </div>
                {group.items.map((it, ii) => {
                  const idx = itemsBefore + ii;
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      key={`${group.type}-${it.id}-${ii}`}
                      onClick={() => onSelect({ ...it, _group: group.type, _label: group.label })}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 text-xs border-b border-border/40 ${isActive ? 'bg-primary/10' : 'hover:bg-muted/30'}`}
                      data-testid={`global-cmdk-item-${group.type}-${idx}`}
                    >
                      <div className="min-w-0 flex-1 flex items-center gap-2">
                        <Icon size={13} className="text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-semibold truncate flex items-center gap-1.5">
                            {it.title}
                            {it.is_auto && (
                              <span className="inline-flex items-center text-[9px] font-bold px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300" title="Auto-generada">
                                <RotateCcw size={8} className="mr-0.5" /> Auto
                              </span>
                            )}
                            {it.credit && (
                              <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300">CxC</span>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate">
                            {it.subtitle}
                            {it.ncf && <span className="ml-2 font-mono">NCF: {it.ncf}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {it.amount !== undefined && (
                          <div className="font-mono font-bold text-xs">RD$ {fmt(it.amount)}</div>
                        )}
                        {it.stock !== undefined && it.stock !== null && (
                          <div className="text-[10px] text-muted-foreground">stock: {it.stock}</div>
                        )}
                        {isActive && <CornerDownLeft size={11} className="inline ml-2 text-primary" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        {results && results.total_results > 0 && (
          <div className="border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground flex items-center justify-between">
            <span>{results.total_results} resultado(s)</span>
            <span className="flex items-center gap-2">
              <kbd className="px-1 border border-border rounded font-mono">↑↓</kbd> navegar
              <kbd className="px-1 border border-border rounded font-mono">↵</kbd> abrir
              <kbd className="px-1 border border-border rounded font-mono">ESC</kbd> cerrar
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
